import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const PurchaseEntry = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingPurchase, setViewingPurchase] = useState(null);
  const [entries, setEntries] = useState([{
    id: Date.now(),
    date: new Date().toISOString().split('T')[0],
    invoiceNo: '',
    itemName: '',
    supplierName: '',
    supplierContact: '',
    supplierAddress: '',
    amount: '',
    gstRate: '18',
    gst: '',
    totalAmount: '',
    paymentMode: 'cash',
    paymentProof: '',
    utrNo: '',
    description: ''
  }]);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(0);
  const [viewingImage, setViewingImage] = useState(null);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Compress image before upload to approximately 50KB
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const TARGET_SIZE_KB = 50;
          const TARGET_SIZE_BYTES = TARGET_SIZE_KB * 1024;

          // Start with smaller dimensions for better compression
          let MAX_WIDTH = 800;
          let MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          // Calculate aspect ratio
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Function to compress with specific quality
          const compressWithQuality = (quality) => {
            return new Promise((resolveBlob) => {
              canvas.toBlob(
                (blob) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  reader.onloadend = () => {
                    resolveBlob({
                      dataUrl: reader.result,
                      size: blob.size
                    });
                  };
                },
                'image/jpeg',
                quality
              );
            });
          };

          // Try different quality levels to get close to target size
          const tryCompress = async () => {
            let quality = 0.7;
            let result = await compressWithQuality(quality);

            // If still too large, reduce quality
            if (result.size > TARGET_SIZE_BYTES) {
              quality = 0.5;
              result = await compressWithQuality(quality);

              if (result.size > TARGET_SIZE_BYTES) {
                quality = 0.3;
                result = await compressWithQuality(quality);

                if (result.size > TARGET_SIZE_BYTES) {
                  quality = 0.2;
                  result = await compressWithQuality(quality);
                }
              }
            }

            resolve(result.dataUrl);
          };

          tryCompress();
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  useEffect(() => {
    // Auto-calculate GST and total amount for all entries
    setEntries(prevEntries =>
      prevEntries.map(entry => {
        if (entry.amount && entry.gstRate) {
          const amount = parseFloat(entry.amount) || 0;
          const gstRate = parseFloat(entry.gstRate) || 0;
          const gstAmount = (amount * gstRate) / 100;
          const total = amount + gstAmount;

          return {
            ...entry,
            gst: gstAmount.toFixed(2),
            totalAmount: total.toFixed(2)
          };
        }
        return entry;
      })
    );
  }, [entries.map(e => `${e.amount}-${e.gstRate}`).join(',')]);

  const fetchPurchases = async () => {
    try {
      const db = getDatabase();
      const purchasesRef = ref(db, 'HTAMS/expenses/purchaseentry');
      const snapshot = await get(purchasesRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const purchasesList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setPurchases(purchasesList.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setPurchases([]);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to fetch purchases');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      try {
        toast.info('Compressing image...');
        const compressedImage = await compressImage(file);
        setEntries(prev => prev.map((entry, i) =>
          i === index ? { ...entry, paymentProof: compressedImage } : entry
        ));
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.error('Failed to compress image');
      }
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    // Validation for numeric fields
    if (name === 'supplierContact') {
      // Only allow digits, max 10 characters
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setEntries(prev => prev.map((entry, i) =>
          i === index ? { ...entry, [name]: numericValue } : entry
        ));
      }
      return;
    }

    if (name === 'amount' || name === 'gstRate') {
      // Only allow numbers and decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setEntries(prev => prev.map((entry, i) => {
        if (i === index) {
          const updatedEntry = { ...entry, [name]: validValue };

          // Auto-calculate GST and total amount
          const amount = parseFloat(name === 'amount' ? validValue : entry.amount) || 0;
          const gstRate = parseFloat(name === 'gstRate' ? validValue : entry.gstRate) || 0;
          const gst = (amount * gstRate) / 100;
          const totalAmount = amount + gst;

          return {
            ...updatedEntry,
            gst: gst.toFixed(2),
            totalAmount: totalAmount.toFixed(2)
          };
        }
        return entry;
      }));
      return;
    }

    if (name === 'utrNo') {
      // Allow alphanumeric for UTR/Cheque number
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }

    // For other fields, trim leading/trailing spaces but allow text
    setEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [name]: value } : entry
    ));
  };

  const addNewEntry = () => {
    const newIndex = entries.length;
    setEntries(prev => [...prev, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      invoiceNo: '',
      itemName: '',
      supplierName: '',
      supplierContact: '',
      supplierAddress: '',
      amount: '',
      gstRate: '18',
      gst: '',
      totalAmount: '',
      paymentMode: 'cash',
      paymentProof: '',
      utrNo: '',
      description: ''
    }]);
    setExpandedEntry(newIndex);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      setExpandedEntry(index > 0 ? index - 1 : 0);
    } else {
      toast.warning('At least one entry is required');
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? index : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;

    // Date validation
    if (!entry.date) {
      toast.error(`Entry ${entryNum}: Date is required`);
      return false;
    }

    // Item name validation
    if (!entry.itemName || entry.itemName.trim() === '') {
      toast.error(`Entry ${entryNum}: Item name is required`);
      return false;
    }

    // Supplier contact validation
    if (!entry.supplierContact || entry.supplierContact.trim() === '') {
      toast.error(`Entry ${entryNum}: Supplier contact is required`);
      return false;
    }

    // Validate contact is 10 digits
    const contactRegex = /^\d{10}$/;
    if (!contactRegex.test(entry.supplierContact)) {
      toast.error(`Entry ${entryNum}: Supplier contact must be 10 digits`);
      return false;
    }

    // Amount validation
    if (!entry.amount || entry.amount.trim() === '') {
      toast.error(`Entry ${entryNum}: Amount is required`);
      return false;
    }

    if (parseFloat(entry.amount) <= 0) {
      toast.error(`Entry ${entryNum}: Amount must be greater than 0`);
      return false;
    }

    // GST Rate validation
    if (!entry.gstRate || entry.gstRate.trim() === '') {
      toast.error(`Entry ${entryNum}: GST Rate is required`);
      return false;
    }

    if (parseFloat(entry.gstRate) < 0) {
      toast.error(`Entry ${entryNum}: GST Rate cannot be negative`);
      return false;
    }

    // Payment mode validation
    if (!entry.paymentMode) {
      toast.error(`Entry ${entryNum}: Payment mode is required`);
      return false;
    }

    // Payment proof validation for cash and online
    if ((entry.paymentMode === 'cash' || entry.paymentMode === 'online') && !entry.paymentProof) {
      toast.error(`Entry ${entryNum}: Payment proof image is required for ${entry.paymentMode} payment`);
      return false;
    }

    // UTR No validation for cheque
    if (entry.paymentMode === 'cheque' && (!entry.utrNo || entry.utrNo.trim() === '')) {
      toast.error(`Entry ${entryNum}: UTR No / Cheque No is required for cheque payment`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        setExpandedEntry(i); // Expand the entry with validation error
        return;
      }
    }

    setSubmitting(true);
    try {
      const db = getDatabase();

      if (editingId) {
        // Update existing purchase (single entry)
        const entry = entries[0];
        const purchaseData = {
          date: entry.date,
          invoiceNo: entry.invoiceNo,
          itemName: entry.itemName,
          supplierName: entry.supplierName,
          supplierContact: entry.supplierContact,
          supplierAddress: entry.supplierAddress,
          amount: parseFloat(entry.amount),
          gstRate: entry.gstRate,
          gst: parseFloat(entry.gst),
          totalAmount: parseFloat(entry.totalAmount),
          paymentMode: entry.paymentMode,
          paymentProof: entry.paymentProof,
          utrNo: entry.utrNo,
          description: entry.description,
          updatedAt: new Date().toISOString(),
          timestamp: Date.now()
        };

        const purchaseRef = ref(db, `HTAMS/expenses/purchaseentry/${editingId}`);
        await update(purchaseRef, purchaseData);
        toast.success('Purchase entry updated successfully!');
        setEditingId(null);
      } else {
        // Create new purchases (multiple entries)
        let successCount = 0;

        for (const entry of entries) {
          const purchaseData = {
            date: entry.date,
            invoiceNo: entry.invoiceNo,
            itemName: entry.itemName,
            supplierName: entry.supplierName,
            supplierContact: entry.supplierContact,
            supplierAddress: entry.supplierAddress,
            amount: parseFloat(entry.amount),
            gstRate: entry.gstRate,
            gst: parseFloat(entry.gst),
            totalAmount: parseFloat(entry.totalAmount),
            paymentMode: entry.paymentMode,
            paymentProof: entry.paymentProof,
            utrNo: entry.utrNo,
            description: entry.description,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            timestamp: Date.now()
          };

          const purchasesRef = ref(db, 'HTAMS/expenses/purchaseentry');
          const newPurchaseRef = push(purchasesRef);
          await set(newPurchaseRef, purchaseData);
          successCount++;
        }

        toast.success(`${successCount} purchase ${successCount > 1 ? 'entries' : 'entry'} added successfully!`);
      }

      setShowForm(false);
      setEntries([{
        id: Date.now(),
        date: new Date().toISOString().split('T')[0],
        invoiceNo: '',
        itemName: '',
        supplierName: '',
        supplierContact: '',
        supplierAddress: '',
        amount: '',
        gstRate: '18',
        gst: '',
        totalAmount: '',
        paymentMode: 'cash',
        paymentProof: '',
        utrNo: '',
        description: ''
      }]);

      fetchPurchases();
    } catch (error) {
      console.error('Error saving purchases:', error);
      toast.error('Failed to save purchase entries');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingId(purchase.id);
    setEntries([{
      id: purchase.id,
      date: purchase.date,
      invoiceNo: purchase.invoiceNo,
      itemName: purchase.itemName || '',
      supplierName: purchase.supplierName,
      supplierContact: purchase.supplierContact || '',
      supplierAddress: purchase.supplierAddress || '',
      amount: purchase.amount.toString(),
      gstRate: purchase.gstRate?.toString() || '18',
      gst: purchase.gst.toString(),
      totalAmount: purchase.totalAmount.toString(),
      paymentMode: purchase.paymentMode || 'cash',
      paymentProof: purchase.paymentProof || '',
      utrNo: purchase.utrNo || '',
      description: purchase.description || ''
    }]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, supplierName) => {
    if (window.confirm(`Are you sure you want to delete purchase entry for "${supplierName}"?`)) {
      try {
        const db = getDatabase();
        const purchaseRef = ref(db, `HTAMS/expenses/purchaseentry/${id}`);
        await remove(purchaseRef);
        toast.success('Purchase entry deleted successfully!');
        fetchPurchases();
      } catch (error) {
        console.error('Error deleting purchase:', error);
        toast.error('Failed to delete purchase entry');
      }
    }
  };

  const handleView = (purchase) => {
    setViewingPurchase(purchase);
  };

  const closeViewModal = () => {
    setViewingPurchase(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowForm(false);
    setEntries([{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      invoiceNo: '',
      itemName: '',
      supplierName: '',
      supplierContact: '',
      supplierAddress: '',
      amount: '',
      gstRate: '18',
      gst: '',
      totalAmount: '',
      paymentMode: 'cash',
      paymentProof: '',
      utrNo: '',
      description: ''
    }]);
  };

  const filteredPurchases = purchases.filter(purchase => {
    const matchesSearch = purchase.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.itemName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(purchase.date) >= new Date(dateFilter.start)) &&
      (!dateFilter.end || new Date(purchase.date) <= new Date(dateFilter.end));
    const matchesPaymentMethod = !paymentMethodFilter || purchase.paymentMode === paymentMethodFilter;
    return matchesSearch && matchesDate && matchesPaymentMethod;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPurchases = filteredPurchases.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, paymentMethodFilter]);

  const exportToExcel = () => {
    if (filteredPurchases.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredPurchases.map((purchase, index) => ({
      'Sr No': index + 1,
      'Date': formatDate(purchase.date),
      'Item Name': purchase.itemName || '-',
      'Invoice No': purchase.invoiceNo || '-',
      'Supplier Name': purchase.supplierName || '-',
      'Supplier Contact': purchase.supplierContact || '-',
      'Supplier Address': purchase.supplierAddress || '-',
      'Amount': purchase.amount,
      'GST Rate (%)': purchase.gstRate || 18,
      'GST Amount': purchase.gst,
      'Total Amount': purchase.totalAmount,
      'Payment Mode': (purchase.paymentMode || 'cash').toUpperCase(),
      'UTR/Cheque No': purchase.utrNo || '-',
      'Description': purchase.description || '-'
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Purchase_Entries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredPurchases.length} entries successfully!`);
  };

  if (loading) return <div className="loading">Loading purchases...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="page-header">
        <h2>Purchase Entry</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Purchase'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Purchase Entry' : 'Add New Purchase Entry'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => {
              const isExpanded = expandedEntry === index;
              return (
                <div
                  key={entry.id}
                  style={{
                    marginBottom: '10px',
                    border: isExpanded ? '2px solid #2c5f2d' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div
                    onClick={() => toggleEntry(index)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: isExpanded ? '#2c5f2d' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <h4 style={{ margin: 0, color: isExpanded ? 'white' : '#2c5f2d', fontWeight: 'bold' }}>
                        Entry {index + 1}
                      </h4>
                      {entry.itemName && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666' }}>
                          {entry.itemName}
                        </span>
                      )}
                      {entry.totalAmount && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666', fontWeight: 'bold' }}>
                          ₹{entry.totalAmount}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!editingId && entries.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEntry(index);
                          }}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 15px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Remove
                        </button>
                      )}
                      <span style={{ color: isExpanded ? 'white' : '#2c5f2d', fontSize: '20px', fontWeight: 'bold' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '15px' }}>
                      <div className="form-grid" style={{ gap: '12px' }}>
                        <div className="form-group">
                          <label>Date <span className="required">*</span></label>
                          <input
                            type="date"
                            name="date"
                            value={entry.date}
                            onChange={(e) => handleInputChange(e, index)}
                            required
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Item Name</label>
                          <input
                            type="text"
                            name="itemName"
                            value={entry.itemName}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter item name"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Invoice No</label>
                          <input
                            type="text"
                            name="invoiceNo"
                            value={entry.invoiceNo}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter invoice number"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Supplier Name</label>
                          <input
                            type="text"
                            name="supplierName"
                            value={entry.supplierName}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter supplier name"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Supplier Contact</label>
                          <input
                            type="text"
                            name="supplierContact"
                            value={entry.supplierContact}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter contact number"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Supplier Address</label>
                          <input
                            type="text"
                            name="supplierAddress"
                            value={entry.supplierAddress}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter supplier address"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>Amount <span className="required">*</span></label>
                          <input
                            type="number"
                            name="amount"
                            value={entry.amount}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter amount"
                            required
                            step="0.01"
                            min="0"
                            className="form-control"
                          />
                        </div>

                        <div className="form-group">
                          <label>GST Rate (%)</label>
                          <select
                            name="gstRate"
                            value={entry.gstRate}
                            onChange={(e) => handleInputChange(e, index)}
                            className="form-control"
                          >
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                            <option value="28">28%</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>GST Amount</label>
                          <input
                            type="text"
                            value={`₹${entry.gst}`}
                            readOnly
                            className="form-control readonly"
                          />
                        </div>

                        <div className="form-group">
                          <label>Total Amount</label>
                          <input
                            type="text"
                            value={`₹${entry.totalAmount}`}
                            readOnly
                            className="form-control readonly total-highlight"
                          />
                        </div>

                        <div className="form-group">
                          <label>Payment Mode</label>
                          <select
                            name="paymentMode"
                            value={entry.paymentMode}
                            onChange={(e) => handleInputChange(e, index)}
                            className="form-control"
                          >
                            <option value="cash">Cash</option>
                            <option value="online">Online</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>

                        {/* Conditional fields based on payment mode */}
                        {(entry.paymentMode === 'cash' || entry.paymentMode === 'online') && (
                          <div className="form-group">
                            <label>Payment Proof (Image)</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, index)}
                              className="form-control"
                            />
                            {entry.paymentProof && (
                              <div className="image-preview" style={{ marginTop: '10px', position: 'relative', display: 'inline-block' }}>
                                <img
                                  src={entry.paymentProof}
                                  alt="Payment Proof"
                                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '5px' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setEntries(prev => prev.map((e, i) => i === index ? { ...e, paymentProof: '' } : e))}
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    lineHeight: '1',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                  }}
                                  title="Remove image"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {entry.paymentMode === 'cheque' && (
                          <div className="form-group">
                            <label>UTR No / Cheque No</label>
                            <input
                              type="text"
                              name="utrNo"
                              value={entry.utrNo}
                              onChange={(e) => handleInputChange(e, index)}
                              placeholder="Enter UTR or Cheque number"
                              className="form-control"
                            />
                          </div>
                        )}

                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            name="description"
                            value={entry.description}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter description or notes"
                            rows="3"
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!editingId && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Purchase' : `Save ${entries.length} Purchase${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by supplier, invoice, or item..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />

        <select
          value={paymentMethodFilter}
          onChange={(e) => setPaymentMethodFilter(e.target.value)}
          className="search-input"
          style={{ cursor: 'pointer' }}
        >
          <option value="">All Payment Methods</option>
          <option value="cash">Cash</option>
          <option value="online">Online</option>
          <option value="cheque">Cheque</option>
        </select>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
            className="date-input"
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
            className="date-input"
          />
        </div>

        {(searchTerm || paymentMethodFilter || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setPaymentMethodFilter('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Item Name</th>
              <th>Invoice No</th>
              <th>Supplier Name</th>
              <th>Amount</th>
              <th>GST</th>
              <th>Total Amount</th>
              <th>Payment Mode</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPurchases.length > 0 ? (
              currentPurchases.map((purchase, index) => (
                <tr key={purchase.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(purchase.date)}</td>
                  <td>{purchase.itemName || '-'}</td>
                  <td>{purchase.invoiceNo || '-'}</td>
                  <td>{purchase.supplierName || '-'}</td>
                  <td>₹{purchase.amount?.toLocaleString()}</td>
                  <td>₹{purchase.gst?.toLocaleString()}</td>
                  <td>₹{purchase.totalAmount?.toLocaleString()}</td>
                  <td>
                    <span className={`payment-badge payment-${purchase.paymentMode || 'cash'}`}>
                      {(purchase.paymentMode || 'cash').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleView(purchase)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(purchase)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(purchase.id, purchase.supplierName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>

        {currentPurchases.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No purchase entries found</h3>
            <p>Add purchase entries using the "Add Purchase Entry" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingPurchase && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Purchase Entry Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date:</label>
                  <span>{formatDate(viewingPurchase.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Item Name:</label>
                  <span>{viewingPurchase.itemName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Invoice No:</label>
                  <span>{viewingPurchase.invoiceNo || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Supplier Name:</label>
                  <span>{viewingPurchase.supplierName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Supplier Contact:</label>
                  <span>{viewingPurchase.supplierContact || 'N/A'}</span>
                </div>
                <div className="detail-item full-width">
                  <label>Supplier Address:</label>
                  <span>{viewingPurchase.supplierAddress || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Amount:</label>
                  <span>₹{viewingPurchase.amount?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>GST Rate:</label>
                  <span>{viewingPurchase.gstRate || 18}%</span>
                </div>
                <div className="detail-item">
                  <label>GST Amount:</label>
                  <span>₹{viewingPurchase.gst?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Total Amount:</label>
                  <span className="highlight-amount">₹{viewingPurchase.totalAmount?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Mode:</label>
                  <span className={`payment-badge payment-${viewingPurchase.paymentMode || 'cash'}`}>
                    {(viewingPurchase.paymentMode || 'cash').toUpperCase()}
                  </span>
                </div>

                {/* Conditional Payment Info */}
                {(viewingPurchase.paymentMode === 'cash' || viewingPurchase.paymentMode === 'online') && viewingPurchase.paymentProof && (
                  <div className="detail-item full-width">
                    <label>Payment Proof:</label>
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={viewingPurchase.paymentProof}
                        alt="Payment Proof"
                        style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '5px', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newWindow = window.open(viewingPurchase.paymentProof, '_blank', 'noopener,noreferrer');
                          if (newWindow) newWindow.opener = null;
                        }}
                      />
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingImage(viewingPurchase.paymentProof);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          👁️ View Full Image
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement('a');
                            link.href = viewingPurchase.paymentProof;
                            link.download = `payment-proof-${viewingPurchase.invoiceNo || 'image'}.jpg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          ⬇️ Download Image
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {viewingPurchase.paymentMode === 'cheque' && viewingPurchase.utrNo && (
                  <div className="detail-item">
                    <label>UTR No / Cheque No:</label>
                    <span>{viewingPurchase.utrNo}</span>
                  </div>
                )}

                {viewingPurchase.description && (
                  <div className="detail-item full-width">
                    <label>Description:</label>
                    <span>{viewingPurchase.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewing Modal */}
      {viewingImage && (
        <div
          className="modal-overlay"
          onClick={() => setViewingImage(null)}
          style={{ zIndex: 1001 }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              padding: '20px',
              overflow: 'auto'
            }}
          >
            <div className="modal-header">
              <h3>Payment Proof Image</h3>
              <button className="modal-close" onClick={() => setViewingImage(null)}>✕</button>
            </div>
            <div style={{
              padding: '20px',
              textAlign: 'center',
              overflow: 'auto'
            }}>
              <img
                src={viewingImage}
                alt="Payment Proof"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              <div style={{ marginTop: '20px' }}>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = viewingImage;
                    link.download = 'payment-proof.jpg';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  ⬇️ Download Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredPurchases.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPurchases.length)} of {filteredPurchases.length} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first page, last page, current page, and pages around current
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-dots">...</span>;
              }
              return null;
            })}

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseEntry;
